import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Upload, X, FileText, Image, User, Building2, Shield,
  Globe, Phone, Clock, ArrowLeft, Check, Loader2, ChevronDown, Search
} from 'lucide-react';
import api from '../services/api';
import { resolveFileUrl } from '@/lib/utils';
import TierBadge, { TierProgress } from '../components/TierBadge';
import Sidebar from '../components/Sidebar';
import CountrySelect from '../components/CountrySelect';
import { useTheme } from '../contexts/ThemeContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Time zones list (for timezone_select field type)
const TIMEZONES = [
  // North America
  { value: 'America/New_York', label: 'USA - New York (Eastern)' },
  { value: 'America/Chicago', label: 'USA - Chicago (Central)' },
  { value: 'America/Denver', label: 'USA - Denver (Mountain)' },
  { value: 'America/Phoenix', label: 'USA - Phoenix (Arizona)' },
  { value: 'America/Los_Angeles', label: 'USA - Los Angeles (Pacific)' },
  { value: 'America/Anchorage', label: 'USA - Anchorage (Alaska)' },
  { value: 'Pacific/Honolulu', label: 'USA - Honolulu (Hawaii)' },
  { value: 'America/Toronto', label: 'Canada - Toronto (Eastern)' },
  { value: 'America/Vancouver', label: 'Canada - Vancouver (Pacific)' },
  { value: 'America/Mexico_City', label: 'Mexico - Mexico City' },
  // South America
  { value: 'America/Sao_Paulo', label: 'Brazil - São Paulo' },
  { value: 'America/Buenos_Aires', label: 'Argentina - Buenos Aires' },
  { value: 'America/Lima', label: 'Peru - Lima' },
  { value: 'America/Bogota', label: 'Colombia - Bogotá' },
  { value: 'America/Santiago', label: 'Chile - Santiago' },
  // Europe
  { value: 'Europe/London', label: 'UK - London (GMT)' },
  { value: 'Europe/Paris', label: 'France - Paris (CET)' },
  { value: 'Europe/Berlin', label: 'Germany - Berlin (CET)' },
  { value: 'Europe/Madrid', label: 'Spain - Madrid (CET)' },
  { value: 'Europe/Rome', label: 'Italy - Rome (CET)' },
  { value: 'Europe/Amsterdam', label: 'Netherlands - Amsterdam (CET)' },
  { value: 'Europe/Zurich', label: 'Switzerland - Zurich (CET)' },
  { value: 'Europe/Moscow', label: 'Russia - Moscow' },
  { value: 'Europe/Istanbul', label: 'Turkey - Istanbul' },
  // Asia
  { value: 'Asia/Dubai', label: 'UAE - Dubai' },
  { value: 'Asia/Mumbai', label: 'India - Mumbai' },
  { value: 'Asia/Kolkata', label: 'India - Kolkata' },
  { value: 'Asia/Singapore', label: 'Singapore' },
  { value: 'Asia/Hong_Kong', label: 'Hong Kong' },
  { value: 'Asia/Shanghai', label: 'China - Shanghai' },
  { value: 'Asia/Tokyo', label: 'Japan - Tokyo' },
  { value: 'Asia/Seoul', label: 'South Korea - Seoul' },
  { value: 'Asia/Jakarta', label: 'Indonesia - Jakarta' },
  { value: 'Asia/Bangkok', label: 'Thailand - Bangkok' },
  // Oceania
  { value: 'Australia/Sydney', label: 'Australia - Sydney' },
  { value: 'Australia/Melbourne', label: 'Australia - Melbourne' },
  { value: 'Australia/Perth', label: 'Australia - Perth' },
  { value: 'Pacific/Auckland', label: 'New Zealand - Auckland' },
  // Africa
  { value: 'Africa/Johannesburg', label: 'South Africa - Johannesburg' },
  { value: 'Africa/Cairo', label: 'Egypt - Cairo' },
  { value: 'Africa/Lagos', label: 'Nigeria - Lagos' }
];

// Languages list (for language_select field type)
const LANGUAGES = [
  'English', 'Spanish', 'Portuguese', 'French', 'German', 
  'Chinese', 'Japanese', 'Arabic', 'Russian', 'Hindi'
];

// Searchable dropdown component (for account types, etc.)
const SearchableSelect = ({ value, onChange, options = [], placeholder = 'Select...', disabled = false, testId = 'searchable-select' }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearch('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const filtered = options.filter(opt =>
    opt.label.toLowerCase().includes(search.toLowerCase())
  );

  const selectedLabel = options.find(o => o.value === value)?.label;

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className={`w-full px-4 py-3 bg-white border border-gray-300 rounded-lg text-left flex items-center justify-between focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
          disabled ? 'bg-gray-100 cursor-not-allowed' : 'cursor-pointer hover:border-gray-400'
        }`}
        data-testid={testId}
      >
        <span className={selectedLabel ? 'text-gray-900' : 'text-gray-400'}>
          {selectedLabel || placeholder}
        </span>
        <ChevronDown className={`w-5 h-5 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg">
          <div className="p-2 border-b border-gray-100">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search..."
                className="w-full pl-9 pr-4 py-2 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                autoFocus
              />
            </div>
          </div>
          <div className="max-h-60 overflow-y-auto">
            {filtered.length > 0 ? (
              filtered.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => { onChange(opt.value); setIsOpen(false); setSearch(''); }}
                  className={`w-full px-4 py-2.5 text-left text-sm hover:bg-gray-50 flex items-center justify-between ${
                    value === opt.value ? 'bg-blue-50 text-blue-700' : 'text-gray-700'
                  }`}
                >
                  <span>{opt.label}</span>
                  {value === opt.value && <Check className="w-4 h-4 text-blue-600" />}
                </button>
              ))
            ) : (
              <div className="px-4 py-3 text-sm text-gray-500 text-center">No results found</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// Dynamic Field Renderer Component
const DynamicField = ({ 
  question, 
  value, 
  onChange, 
  accountTypes = [], 
  companyTypes = [],
  disabled = false 
}) => {
  const fieldKey = question.field_key;
  const label = question.label;
  const fieldType = question.field_type;
  const placeholder = question.placeholder || '';
  const isRequired = question.is_required;
  const isReadonly = question.is_readonly || disabled;
  const options = question.options || [];

  const inputClass = isReadonly 
    ? "w-full px-4 py-3 bg-gray-100 border border-gray-300 rounded-lg text-gray-500 cursor-not-allowed"
    : "w-full px-4 py-3 bg-white border border-gray-300 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500";

  const renderField = () => {
    switch (fieldType) {
      case 'email':
        return (
          <input
            type="email"
            value={value || ''}
            onChange={(e) => onChange(fieldKey, e.target.value)}
            className={inputClass}
            placeholder={placeholder}
            disabled={isReadonly}
            data-testid={`field-${fieldKey}`}
          />
        );
      
      case 'phone':
        return (
          <input
            type="tel"
            value={value || ''}
            onChange={(e) => onChange(fieldKey, e.target.value)}
            className={inputClass}
            placeholder={placeholder || '+1 234 567 8900'}
            disabled={isReadonly}
            data-testid={`field-${fieldKey}`}
          />
        );
      
      case 'textarea':
        return (
          <textarea
            value={value || ''}
            onChange={(e) => onChange(fieldKey, e.target.value)}
            rows={3}
            className={`${inputClass} resize-none`}
            placeholder={placeholder}
            disabled={isReadonly}
            data-testid={`field-${fieldKey}`}
          />
        );
      
      case 'url':
        return (
          <input
            type="url"
            value={value || ''}
            onChange={(e) => onChange(fieldKey, e.target.value)}
            className={inputClass}
            placeholder={placeholder || 'https://example.com'}
            disabled={isReadonly}
            data-testid={`field-${fieldKey}`}
          />
        );
      
      case 'country':
        return (
          <CountrySelect
            value={value || ''}
            onChange={(country) => onChange(fieldKey, country)}
            testId={`field-${fieldKey}`}
          />
        );
      
      case 'account_type_select':
        return (
          <SearchableSelect
            value={value || ''}
            onChange={(val) => onChange(fieldKey, val)}
            options={accountTypes.map(at => ({
              value: at.name.toLowerCase().replace('/', '_').replace(' ', '_'),
              label: at.name
            }))}
            placeholder="Search and select account type"
            disabled={isReadonly}
            testId={`field-${fieldKey}`}
          />
        );
      
      case 'company_type_select':
        return (
          <select
            value={value || ''}
            onChange={(e) => onChange(fieldKey, e.target.value)}
            className={inputClass}
            disabled={isReadonly}
            data-testid={`field-${fieldKey}`}
          >
            <option value="">Select company type</option>
            {companyTypes.map(ct => (
              <option key={ct.id} value={ct.name}>
                {ct.name}
              </option>
            ))}
          </select>
        );
      
      case 'language_select':
        return (
          <select
            value={value || ''}
            onChange={(e) => onChange(fieldKey, e.target.value)}
            className={inputClass}
            disabled={isReadonly}
            data-testid={`field-${fieldKey}`}
          >
            <option value="">Select language</option>
            {LANGUAGES.map(lang => (
              <option key={lang} value={lang}>{lang}</option>
            ))}
          </select>
        );
      
      case 'timezone_select':
        return (
          <select
            value={value || ''}
            onChange={(e) => onChange(fieldKey, e.target.value)}
            className={inputClass}
            disabled={isReadonly}
            data-testid={`field-${fieldKey}`}
          >
            <option value="">Select timezone</option>
            {TIMEZONES.map(tz => (
              <option key={tz.value} value={tz.value}>{tz.label}</option>
            ))}
          </select>
        );
      
      case 'select':
        return (
          <select
            value={value || ''}
            onChange={(e) => onChange(fieldKey, e.target.value)}
            className={inputClass}
            disabled={isReadonly}
            data-testid={`field-${fieldKey}`}
          >
            <option value="">Select...</option>
            {options.map(opt => (
              <option key={opt} value={opt}>{opt}</option>
            ))}
          </select>
        );
      
      // File uploads are handled separately in the document upload section
      case 'file':
      case 'image_upload':
        return null;
      
      default: // text
        return (
          <input
            type="text"
            value={value || ''}
            onChange={(e) => onChange(fieldKey, e.target.value)}
            className={inputClass}
            placeholder={placeholder}
            disabled={isReadonly}
            data-testid={`field-${fieldKey}`}
          />
        );
    }
  };

  // Skip file upload fields (handled separately)
  if (fieldType === 'file' || fieldType === 'image_upload') {
    return null;
  }

  return (
    <div className={fieldType === 'textarea' ? 'md:col-span-2' : ''}>
      <label className="block text-sm font-medium text-gray-700 mb-1">
        {label} {isRequired && <span className="text-red-500">*</span>}
      </label>
      {renderField()}
    </div>
  );
};

const TierVerificationPage = () => {
  const { tier } = useParams();
  const navigate = useNavigate();
  const { userTheme } = useTheme();
  const [tierStatus, setTierStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [isBlockingError, setIsBlockingError] = useState(false); // True if error should hide the form
  
  // Get theme colors
  const pageStyles = userTheme?.page || {};
  const cardStyles = userTheme?.cards || {};
  const buttonStyles = userTheme?.buttons || {};
  const primaryColor = buttonStyles.primaryColor || '#2B4B6A';
  
  // Dynamic form data - single state object for all form fields
  const [formData, setFormData] = useState({});
  
  // Documents for file uploads
  const [documents, setDocuments] = useState([]);
  const [uploadingDoc, setUploadingDoc] = useState(null);
  
  // Account types and company types
  const [accountTypes, setAccountTypes] = useState([]);
  const [companyTypes, setCompanyTypes] = useState([]);
  
  // Dynamic tier questions from database
  const [tierQuestions, setTierQuestions] = useState({ sections: [] });
  const [documentTypes, setDocumentTypes] = useState([]);
  
  // Role-specific questions for Bronze tier
  const [roleQuestions, setRoleQuestions] = useState({});
  const [roleQuestionAnswers, setRoleQuestionAnswers] = useState({});

  // Handler to update form data
  const handleFieldChange = (fieldKey, value) => {
    setFormData(prev => ({ ...prev, [fieldKey]: value }));
  };

  useEffect(() => {
    fetchData();
  }, [tier]);

  // Fetch role-specific questions when account type changes
  useEffect(() => {
    if (tier === 'bronze' && formData.account_type) {
      fetchRoleQuestions(formData.account_type);
    }
  }, [tier, formData.account_type]);

  const fetchRoleQuestions = async (accountType) => {
    try {
      const res = await api.get(`/tiers/bronze-questions?account_type=${encodeURIComponent(accountType)}`);
      setRoleQuestions(res.data.questions || {});
    } catch (err) {
      console.error('Failed to fetch role questions:', err);
    }
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      setError('');

      // Fetch tier questions from database
      const tierQuestionsPromise = api.get(`/tier-questions/${tier}`).catch(() => ({ data: { sections: [] } }));

      // Business info the user already saved on their directory listing, keyed by
      // tier field_key. Lets an owner avoid re-typing company details here.
      const businessPrefillPromise = api
        .get('/directory/my-business-prefill')
        .catch(() => ({ data: { prefill: {} } }));

      const [statusRes, accountTypesRes, companyTypesRes, tierQuestionsRes, businessRes] = await Promise.all([
        api.get('/tiers/my-status'),
        api.get('/account-types'),
        api.get('/company-types'),
        tierQuestionsPromise,
        businessPrefillPromise
      ]);
      
      setTierStatus(statusRes.data);
      setAccountTypes(accountTypesRes.data);
      setCompanyTypes(companyTypesRes.data);
      setTierQuestions(tierQuestionsRes.data);
      
      // Build document types from tier questions (for file upload fields)
      if (tierQuestionsRes.data?.sections?.length > 0) {
        const docTypes = [];
        tierQuestionsRes.data.sections.forEach(section => {
          section.questions?.forEach(q => {
            if (q.is_document_upload || q.field_type === 'file' || q.field_type === 'image_upload') {
              docTypes.push({
                id: q.field_key,
                label: q.label,
                required: q.is_required,
                category: q.document_category || section.section_key
              });
            }
          });
        });
        if (docTypes.length > 0) {
          setDocumentTypes(docTypes);
        }
      }

      // Check if user can submit this tier
      const canSubmitKey = `can_submit_${tier}`;
      if (!statusRes.data[canSubmitKey]) {
        if (statusRes.data.tier_status === 'pending') {
          setError(`Your ${statusRes.data.tier} tier verification is pending admin approval.`);
        } else {
          setError(`You cannot submit for ${tier} tier at this time.`);
        }
        setIsBlockingError(true); // This is a blocking error - hide the form
        setLoading(false);
        return;
      }

      // Pre-fill form with existing data based on tier
      const data = statusRes.data;
      const initialFormData = {};

      // Detect country from IP for auto-fill
      let detectedCountry = '';
      try {
        const geoRes = await fetch('https://ipapi.co/json/');
        if (geoRes.ok) {
          const geoData = await geoRes.json();
          detectedCountry = geoData.country_name || '';
        }
      } catch (e) { /* ignore - geo detection is best-effort */ }
      
      // Pre-fill based on tier
      if (tier === 'bronze') {
        initialFormData.first_name = data.first_name || '';
        initialFormData.last_name = data.last_name || '';
        initialFormData.email = data.email || '';
        initialFormData.account_type = data.account_type || '';
        initialFormData.phone = data.phone || '';
        initialFormData.whatsapp = data.whatsapp || '';
        initialFormData.description = data.description || '';

        // Auto-detect country from IP if not already set
        initialFormData.country = data.country || detectedCountry;
      } else if (tier === 'silver') {
        const company = data.company || {};
        initialFormData.profile_photo = company.profile_photo || '';
        initialFormData.job_title = company.job_title || '';
        initialFormData.phone_number = company.phone_number || '';
        initialFormData.preferred_language = company.preferred_language || '';
        initialFormData.timezone = company.timezone || '';
        initialFormData.company_legal_name = company.company_legal_name || '';
        initialFormData.country_of_incorporation = company.country_of_incorporation || '';
        initialFormData.company_address = company.company_address || '';
        initialFormData.company_website = company.company_website || 'https://';
        initialFormData.company_type = company.company_type || '';

        // Auto-detect timezone, language, and country from browser/IP if not already set
        if (!initialFormData.timezone) {
          try {
            initialFormData.timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || '';
          } catch (e) { /* ignore */ }
        }
        if (!initialFormData.preferred_language) {
          const browserLang = navigator.language?.split('-')[0] || '';
          const langMap = { en: 'English', es: 'Spanish', pt: 'Portuguese', fr: 'French', de: 'German', zh: 'Chinese', ja: 'Japanese', ar: 'Arabic', ru: 'Russian', hi: 'Hindi' };
          initialFormData.preferred_language = langMap[browserLang] || '';
        }
        if (!initialFormData.country_of_incorporation) {
          initialFormData.country_of_incorporation = detectedCountry;
        }
      } else if (tier === 'gold') {
        initialFormData.nationality = data.nationality || '';
        initialFormData.tax_id = data.tax_id || '';
      }
      
      // Fill from the directory listing ONLY where the tier form is still blank, so
      // answers already saved against the verification always win.
      const businessPrefill = businessRes?.data?.prefill || {};
      Object.entries(businessPrefill).forEach(([key, value]) => {
        const current = initialFormData[key];
        const isBlank = current === undefined || current === null
          || String(current).trim() === '' || String(current).trim() === 'https://';
        if (isBlank && String(value).trim()) initialFormData[key] = value;
      });

      setFormData(initialFormData);
      
      // Documents - filter by current tier only (don't show previous tier docs)
      if (data.verification_documents) {
        const tierDocs = data.verification_documents.filter(doc => {
          // If doc has a tier field, filter by it
          if (doc.tier) return doc.tier === tier;
          // Otherwise, match against document types for this tier's questions
          const tierDocKeys = [];
          tierQuestionsRes.data?.sections?.forEach(section => {
            section.questions?.forEach(q => {
              if (q.is_document_upload || q.field_type === 'file' || q.field_type === 'image_upload') {
                tierDocKeys.push(q.field_key);
              }
            });
          });
          return tierDocKeys.length === 0 || tierDocKeys.includes(doc.document_type);
        });
        setDocuments(tierDocs);
      }
      
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load verification form');
      setIsBlockingError(true); // Network/server errors are blocking
    } finally {
      setLoading(false);
    }
  };

  // Dynamic form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');

    // Validate required fields from tier questions
    const missingFields = [];
    tierQuestions.sections?.forEach(section => {
      section.questions?.forEach(q => {
        if (q.is_required && !q.is_document_upload && q.field_type !== 'file' && q.field_type !== 'image_upload') {
          if (!formData[q.field_key]) {
            missingFields.push({ key: q.field_key, label: q.label });
          }
        }
      });
    });

    if (missingFields.length > 0) {
      const errorMsg = `Please fill in required fields: ${missingFields.slice(0, 3).map(f => f.label).join(', ')}${missingFields.length > 3 ? '...' : ''}`;
      setError(errorMsg);
      toast.error(errorMsg);
      setSubmitting(false);
      // Scroll to error message
      window.scrollTo({ top: 0, behavior: 'smooth' });
      // Try to focus the first missing field
      const firstMissingField = document.querySelector(`[data-testid="field-${missingFields[0].key}"]`);
      if (firstMissingField) {
        setTimeout(() => firstMissingField.focus(), 100);
      }
      return;
    }

    // Check required documents
    const uploadedTypes = documents.map(d => d.document_type);
    const missingDocs = documentTypes
      .filter(dt => dt.required && !uploadedTypes.includes(dt.id))
      .map(dt => dt.label);
    
    if (missingDocs.length > 0) {
      const errorMsg = `Please upload required documents: ${missingDocs.join(', ')}`;
      setError(errorMsg);
      toast.error(errorMsg);
      setSubmitting(false);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    // Validate role-specific questions for bronze
    if (tier === 'bronze') {
      const requiredQuestions = [];
      Object.entries(roleQuestions).forEach(([category, questions]) => {
        questions.forEach(q => {
          if (q.is_required && !roleQuestionAnswers[q.id]) {
            requiredQuestions.push(q.question_text);
          }
        });
      });
      
      if (requiredQuestions.length > 0) {
        const errorMsg = 'Please answer all required questions';
        setError(errorMsg);
        toast.error(errorMsg);
        setSubmitting(false);
        window.scrollTo({ top: 0, behavior: 'smooth' });
        return;
      }
    }

    try {
      // Submit based on tier
      const submitData = { ...formData };
      
      if (tier === 'bronze') {
        submitData.role_answers = roleQuestionAnswers;
        await api.post('/tiers/submit/bronze', submitData);
      } else if (tier === 'silver') {
        await api.post('/tiers/submit/silver', submitData);
      } else if (tier === 'gold') {
        await api.post('/tiers/submit/gold', submitData);
      }
      
      toast.success(`${tier.charAt(0).toUpperCase() + tier.slice(1)} verification submitted!`);
      navigate('/admin/dashboard');
    } catch (err) {
      const errorMsg = err.response?.data?.detail || 'Submission failed';
      setError(errorMsg);
      toast.error(errorMsg);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setSubmitting(false);
    }
  };

  // Handle logo upload for Silver
  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const uploadData = new FormData();
    uploadData.append('file', file);

    try {
      const res = await api.post('/tiers/silver/upload-logo', uploadData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setFormData(prev => ({ ...prev, profile_photo: res.data.url }));
      toast.success('Logo uploaded!');
    } catch (err) {
      toast.error('Failed to upload logo');
    }
  };

  // Handle document upload
  const handleDocumentUpload = async (documentType, e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploadingDoc(documentType);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const res = await api.post(
        `/tiers/gold/upload-document?document_type=${documentType}&tier=${tier}`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      setDocuments(prev => [...prev, {
        id: res.data.id,
        document_type: documentType,
        file_url: res.data.url,
        original_filename: file.name
      }]);
      toast.success('Document uploaded!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to upload document');
    } finally {
      setUploadingDoc(null);
    }
  };

  // Delete document
  const handleDeleteDocument = async (docId) => {
    try {
      await api.delete(`/tiers/gold/document/${docId}`);
      setDocuments(prev => prev.filter(d => d.id !== docId));
      toast.success('Document deleted');
    } catch (err) {
      toast.error('Failed to delete document');
    }
  };

  const getTierInfo = () => {
    switch (tier) {
      case 'bronze':
        return {
          title: 'Bronze Verification',
          description: 'Complete your basic profile information to unlock platform features.',
          icon: <User className="w-8 h-8 text-orange-500" />
        };
      case 'silver':
        return {
          title: 'Silver Verification',
          description: 'Verify your business details to submit LOI requests and post deals.',
          icon: <Building2 className="w-8 h-8 text-gray-500" />
        };
      case 'gold':
        return {
          title: 'Gold Verification',
          description: 'Complete KYC/KYB verification for premium features and priority support.',
          icon: <Shield className="w-8 h-8 text-amber-500" />
        };
      default:
        return { title: 'Verification', description: '', icon: null };
    }
  };

  const tierInfo = getTierInfo();

  if (loading) {
    return (
      <Sidebar>
        <div className="min-h-screen flex items-center justify-center">
          <Loader2 className="w-12 h-12 animate-spin" style={{ color: primaryColor }} />
        </div>
      </Sidebar>
    );
  }

  return (
    <Sidebar>
      <div className="p-4 lg:p-6">
        <div className="max-w-3xl mx-auto">
          {/* Back Button */}
          <button
            onClick={() => navigate('/admin/dashboard')}
            className="flex items-center gap-2 text-gray-500 hover:text-gray-700 mb-6 transition"
          >
            <ArrowLeft className="w-5 h-5" />
            Back to Dashboard
          </button>

          {/* Header */}
          <div className="text-center mb-8">
            <div 
              className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 border"
              style={{ 
                backgroundColor: `${primaryColor}10`,
                borderColor: `${primaryColor}30`
              }}
            >
              {tierInfo.icon}
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">{tierInfo.title}</h1>
            <p className="text-gray-500">{tierInfo.description}</p>
            
            {tierStatus && (
              <div className="flex justify-center mt-6">
                <TierProgress currentTier={tierStatus.approved_tier || 'none'} tierStatus={tierStatus.approved_tier && tierStatus.approved_tier !== 'none' ? 'approved' : tierStatus.tier_status} />
              </div>
            )}
          </div>

          {/* Error Message - Blocking errors show return link, validation errors just show message */}
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6 text-center">
              {error}
              {isBlockingError && (
                <button
                  onClick={() => navigate('/admin/dashboard')}
                  className="block mx-auto mt-3 text-sm text-red-600 hover:text-red-800 underline"
                >
                  Return to Dashboard
                </button>
              )}
          </div>
        )}

        {/* Dynamic Form - Renders questions from database (show form unless it's a blocking error) */}
        {!isBlockingError && tierQuestions.sections?.length > 0 && (
          <form onSubmit={handleSubmit} className="space-y-6">
            {tierQuestions.sections.map(section => (
              <div key={section.section_key} className="bg-white rounded-2xl p-6 border border-gray-200 shadow-sm">
                <h2 className="text-xl font-semibold text-gray-900 mb-6 flex items-center gap-2">
                  {section.section_key === 'user_profile' && <User className="w-5 h-5 text-orange-500" />}
                  {section.section_key === 'individual_profile' && <User className="w-5 h-5 text-gray-500" />}
                  {section.section_key === 'company_details' && <Building2 className="w-5 h-5 text-gray-500" />}
                  {section.section_key === 'kyc' && <Shield className="w-5 h-5 text-amber-500" />}
                  {section.section_key === 'kyb' && <Building2 className="w-5 h-5 text-amber-500" />}
                  {section.section_label}
                </h2>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {section.questions?.filter(q => q.field_type !== 'file' && q.field_type !== 'image_upload' && !q.is_document_upload).map(question => (
                    <DynamicField
                      key={question.field_key}
                      question={question}
                      value={formData[question.field_key]}
                      onChange={handleFieldChange}
                      accountTypes={accountTypes}
                      companyTypes={companyTypes}
                      disabled={question.is_readonly}
                    />
                  ))}
                </div>

                {/* Document Uploads for this section */}
                {section.questions?.filter(q => q.field_type === 'file' || q.field_type === 'image_upload' || q.is_document_upload).length > 0 && (
                  <div className="mt-6 pt-6 border-t border-gray-100">
                    <h3 className="text-sm font-medium text-gray-700 mb-4">Document Uploads</h3>
                    <div className="space-y-4">
                      {section.questions?.filter(q => q.field_type === 'file' || q.field_type === 'image_upload' || q.is_document_upload).map(docQuestion => {
                        const existingDoc = documents.find(d => d.document_type === docQuestion.field_key);
                        
                        return (
                          <div 
                            key={docQuestion.field_key}
                            className={`p-4 border-2 border-dashed rounded-xl transition-all ${
                              existingDoc ? 'border-green-300 bg-green-50' : 'border-gray-300 hover:border-gray-400'
                            }`}
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-3">
                                {existingDoc ? (
                                  <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                                    <Check className="w-5 h-5 text-green-600" />
                                  </div>
                                ) : (
                                  <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center">
                                    {docQuestion.field_type === 'image_upload' ? (
                                      <Image className="w-5 h-5 text-gray-400" />
                                    ) : (
                                      <FileText className="w-5 h-5 text-gray-400" />
                                    )}
                                  </div>
                                )}
                                <div>
                                  <p className="font-medium text-gray-900">
                                    {docQuestion.label}
                                    {docQuestion.is_required && <span className="text-red-500 ml-1">*</span>}
                                  </p>
                                  {existingDoc && (
                                    <p className="text-sm text-green-600">Uploaded</p>
                                  )}
                                </div>
                              </div>
                              
                              <div className="flex items-center gap-2">
                                {existingDoc && (
                                  <a
                                    href={resolveFileUrl(existingDoc.file_url, BACKEND_URL)}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="px-3 py-1.5 text-sm text-blue-600 hover:bg-blue-50 rounded-lg"
                                  >
                                    View
                                  </a>
                                )}
                                
                                <label className="cursor-pointer">
                                  <input
                                    type="file"
                                    className="hidden"
                                    accept={docQuestion.field_type === 'image_upload' ? 'image/*' : '.pdf,.jpg,.jpeg,.png,.doc,.docx'}
                                    onChange={(e) => handleDocumentUpload(docQuestion.field_key, e)}
                                    disabled={uploadingDoc === docQuestion.field_key}
                                  />
                                  <span 
                                    className="px-4 py-2 text-sm font-medium rounded-lg transition-colors flex items-center gap-2"
                                    style={{ backgroundColor: primaryColor, color: 'white' }}
                                  >
                                    {uploadingDoc === docQuestion.field_key ? (
                                      <>
                                        <Loader2 className="w-4 h-4 animate-spin" />
                                        Uploading...
                                      </>
                                    ) : (
                                      <>
                                        <Upload className="w-4 h-4" />
                                        {existingDoc ? 'Replace' : 'Upload'}
                                      </>
                                    )}
                                  </span>
                                </label>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            ))}

            {/* Role-specific questions for Bronze */}
            {tier === 'bronze' && Object.keys(roleQuestions).length > 0 && (
              <div className="bg-white rounded-2xl p-6 border border-gray-200 shadow-sm">
                <h2 className="text-xl font-semibold text-gray-900 mb-6">Additional Questions</h2>
                
                {Object.entries(roleQuestions).map(([category, questions]) => (
                  <div key={category} className="space-y-4">
                    {questions.map(q => (
                      <div key={q.id}>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          {q.question_text}
                          {q.is_required && <span className="text-red-500 ml-1">*</span>}
                        </label>
                        {q.field_type === 'select' || q.field_type === 'multi_select' ? (
                          <select
                            value={roleQuestionAnswers[q.id] || ''}
                            onChange={(e) => setRoleQuestionAnswers(prev => ({ ...prev, [q.id]: e.target.value }))}
                            className="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg"
                          >
                            <option value="">Select...</option>
                            {q.options?.map(opt => (
                              <option key={opt} value={opt}>{opt}</option>
                            ))}
                          </select>
                        ) : q.field_type === 'textarea' ? (
                          <textarea
                            value={roleQuestionAnswers[q.id] || ''}
                            onChange={(e) => setRoleQuestionAnswers(prev => ({ ...prev, [q.id]: e.target.value }))}
                            rows={3}
                            className="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg resize-none"
                          />
                        ) : (
                          <input
                            type="text"
                            value={roleQuestionAnswers[q.id] || ''}
                            onChange={(e) => setRoleQuestionAnswers(prev => ({ ...prev, [q.id]: e.target.value }))}
                            className="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg"
                          />
                        )}
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            )}

            {/* Submit Button */}
            <button
              type="submit"
              disabled={submitting}
              className="w-full py-4 rounded-xl font-semibold text-white transition-all flex items-center justify-center gap-2 disabled:opacity-50"
              style={{ backgroundColor: primaryColor }}
              data-testid="submit-verification"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Submitting...
                </>
              ) : (
                <>
                  <Check className="w-5 h-5" />
                  Submit {tier.charAt(0).toUpperCase() + tier.slice(1)} Verification
                </>
              )}
            </button>
          </form>
        )}

        {/* Fallback for no questions loaded */}
        {!isBlockingError && (!tierQuestions.sections || tierQuestions.sections.length === 0) && !loading && (
          <div className="bg-white rounded-2xl p-8 border border-gray-200 text-center">
            <Shield className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-700 mb-2">Verification Form Not Available</h3>
            <p className="text-gray-500">The verification questions for this tier have not been configured yet. Please contact support.</p>
          </div>
        )}
        </div>
      </div>
    </Sidebar>
  );
};

export default TierVerificationPage;
